import { useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useMatch } from 'react-router-dom'

import { IContinuousRoute } from '$/types'
import { useGetActiveSystemScopeFilterProfileQuery } from 'app/services/api/filterprofiles'
import { useGetQRAAssessmentQuery } from 'app/services/api/qra/assessments'
import { useGetQRASavedStateQuery } from 'app/services/api/qra/savedstates'
import { QRAViewType } from 'common/utils/navigation/qraNavUtils'

import { SavedStateAssessmentOperators } from 'features/RiskManagement/Assessments/Components/SavedStateAssesssmentOperators'
import SavedStateInfoButton from 'features/RiskManagement/Assessments/Components/SavedStateInfoButton'
import { SavedStateInfoHeaderText } from 'features/RiskManagement/Assessments/Components/SavedStateInfoHeaderText'
import SavedStateToolbar from 'features/RiskManagement/Assessments/Components/Toolbar/SavedStateToolbar'
import WISInfoButton from 'features/RiskManagement/Assessments/Scenarios/WISInfoButton'
import { WISAssessmentStatusCode } from 'features/RiskManagement/Assessments/WISAssessmentStatusCode'
import LineViewBreadcrumb from 'features/RiskManagement/Components/LineViewBreadcrumb'
import RiskViewShell from 'features/RiskManagement/Components/RiskViewShell'

import { SavedStatePageContent } from './SavedStatePageContent'
import { ISavedStateViewProps } from './types'

const LineView = ({
  qraBasePath,
  stateId,
  assessmentId
}: ISavedStateViewProps) => {
  const matchState = useMatch(
    '/risk-management/assessments/states/:stateId/line/:lineId/*'
  )
  const matchStateWithAssessment = useMatch(
    '/risk-management/assessments/states/:stateId/assessments/:assessmentId/line/:lineId/*'
  )

  const url = matchStateWithAssessment?.pathnameBase ?? matchState!.pathnameBase

  const params = matchStateWithAssessment?.params.assessmentId
    ? matchStateWithAssessment?.params
    : matchState?.params

  const isTableView = globalThis.location.pathname.includes(QRAViewType.Table)

  const { currentData: currentState, status: stateStatus } =
    useGetQRASavedStateQuery(stateId ?? skipToken)

  const { currentData: currentAssessmentWithScope, status: assessmentStatus } =
    useGetQRAAssessmentQuery((assessmentId as unknown as string) ?? skipToken)

  const { systemScopeFilterProfile } =
    useGetActiveSystemScopeFilterProfileQuery()

  const currentAssessment = currentState?.assessments?.find(
    (assessment) => assessment.id.toString() === assessmentId
  )

  const isCompletedAssessment =
    currentAssessment?.statusCode === WISAssessmentStatusCode.Completed

  const isErroredAssessment =
    currentAssessment?.statusCode === WISAssessmentStatusCode.Error

  const selectedLine = (): IContinuousRoute | undefined => {
    const tempLines = systemScopeFilterProfile?.continuousRoutes.filter(
      (route) => route.id.toString() === params?.lineId
    )

    return tempLines?.[0]
  }

  const isAssessmentQueryDone =
    !assessmentId || ['fulfilled', 'rejected'].includes(assessmentStatus)

  const isStateQueryDone = stateStatus === 'fulfilled'
  const isLoading = !(isAssessmentQueryDone && isStateQueryDone)

  const memoizedPageContent = useMemo(() => {
    return (
      <SavedStatePageContent
        viewType='line'
        qraBasePath={qraBasePath}
        qraViewBasePath={url}
        stateId={stateId}
        assessmentId={assessmentId}
        currentState={currentState}
        currentAssessment={currentAssessment}
        isLoading={isLoading}
        isCompletedAssessment={isCompletedAssessment}
        lineId={params?.lineId}
      />
    )
  }, [
    assessmentId,
    currentAssessment,
    currentState,
    isCompletedAssessment,
    isLoading,
    params?.lineId,
    qraBasePath,
    stateId,
    url
  ])

  const pageHeaderText = `State ${currentState?.name ?? stateId}`

  const asterisk = currentAssessment?.isPublished ? '' : '*'
  const pageHeaderAssessmentText = currentAssessment
    ? currentAssessment.name + asterisk
    : undefined

  const titleSuffix = pageHeaderAssessmentText ? (
    <>
      <span className='mx-1 text-2xl'>/</span>
      <span className='text-lg text-gray-900'>
        {pageHeaderAssessmentText}
      </span>
    </>
  ) : undefined

  const assessmentParams = matchStateWithAssessment?.params
  const systemViewPath = assessmentParams
    ? `${qraBasePath}/states/${assessmentParams.stateId}/assessments/${assessmentParams.assessmentId}`
    : `${qraBasePath}/states/${params?.stateId}`

  const pageSubheading = (
    <LineViewBreadcrumb
      systemViewPath={`${systemViewPath}/${
        isTableView ? QRAViewType.Table : QRAViewType.Graph
      }`}
      line={selectedLine()}
      continuousRouteId={params?.lineId ?? ''}
    />
  )

  return (
    <RiskViewShell
      breadcrumbs={pageSubheading}
      title={pageHeaderText}
      titleSuffix={titleSuffix}
      actions={
        <div className='flex items-center'>
          <div className='flex items-center text-gray-700'>
            <SavedStateInfoHeaderText
              stateOrAssessment={currentAssessment ?? currentState}
            />
            {assessmentId == null ? (
              <SavedStateInfoButton />
            ) : (
              <WISInfoButton
                qraName={currentState?.name}
                currentAssessment={currentAssessmentWithScope}
              />
            )}
          </div>
          {(isCompletedAssessment || isErroredAssessment) && (
            <SavedStateAssessmentOperators
              assessmentId={currentAssessment?.id.toString()}
            />
          )}
        </div>
      }
      toolbar={
        <SavedStateToolbar
          qraBasePath={qraBasePath}
          qraViewBasePath={url}
          stateId={currentState?.id.toString()}
          baselineId={currentState?.baselineId}
          assessmentId={currentAssessment?.id.toString()}
        />
      }
    >
      {memoizedPageContent}
    </RiskViewShell>
  )
}

export default LineView
